//src/firebase/memberships.ts

import { collectionGroup, doc, query, where, type DocumentData } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/firebase'
import { Account, AccountConverter } from '@/types/Account'
import { UseCustomerStore } from '@/stores/CustomerStore'

export const ownMembershipQuery = (uid: string) =>
  query(collectionGroup(db, 'Members'), where('uid', '==', uid))

export const MemberAccountConverter = {
  fromFirestore(snapshot: Parameters<typeof AccountConverter.fromFirestore>[0]) {
    const member = snapshot.data()
    const schoolId = snapshot.ref.parent.parent!.id
    // Stored references are explicit. Never invent a school copy of a personal file.
    const profile = (member.profile ?? {}) as DocumentData
    const profileSnapshot = Object.create(snapshot) as typeof snapshot
    profileSnapshot.data = () => ({
      ...profile,
      storeName: [schoolId],
      storeAdmin: { [schoolId]: member },
      admin: { roles: ['User'] },
      firebaseInfo: { ...profile.firebaseInfo, __id: snapshot.id }
    })
    const account = AccountConverter.fromFirestore(profileSnapshot)
    account.contextSchoolId = schoolId
    return account
  },
  toFirestore(account: Account) {
    return AccountConverter.toFirestore(account)
  }
}

export function schoolAccountDoc(uid: string, schoolId = UseCustomerStore().selectedStoreCode) {
  return doc(db, `StoresCustomers/${schoolId}/Members/${uid}`).withConverter(MemberAccountConverter)
}

/**
 * Dates as {seconds, nanoseconds} for the trip through the callable. Seconds
 * are floored so nanoseconds are never negative (dates before 1970, e.g. a
 * date of birth); an empty or invalid date (an Invalid Date from a cleared
 * field) is sent as no date.
 */
function callableDates(value: unknown): unknown {
  if (value instanceof Date) {
    const ms = value.getTime()
    if (!Number.isFinite(ms)) return null
    const seconds = Math.floor(ms / 1000)
    return { seconds, nanoseconds: (ms - seconds * 1000) * 1_000_000 }
  }
  if (Array.isArray(value)) return value.map(callableDates)
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, callableDates(item)])
    )
  return value
}

export async function saveAccountProfile(account: Account, schoolId: string, create = false) {
  const profile = AccountConverter.toFirestore(account)
  return (
    await httpsCallable(
      functions,
      'saveSchoolAccountProfile'
    )({
      schoolId,
      uid: account.getId(),
      profile: callableDates(profile),
      create,
      membership: account.getAdminForStore(schoolId)
    })
  ).data
}

export async function saveSchoolDocumentChange(
  uid: string,
  change: { add?: unknown; remove?: unknown }
) {
  return (
    await httpsCallable(
      functions,
      'saveSchoolAccountDocuments'
    )({
      uid,
      schoolId: UseCustomerStore().selectedStoreCode,
      ...change
    })
  ).data
}

export function applyMemberships(account: Account, memberships: DocumentData[]) {
  account.storeName = memberships.map((member) => member.schoolId)
  account.storeAdmin = Object.fromEntries(memberships.map((member) => [member.schoolId, member]))
  return account
}

// ── A member's login email (functions/src/accounts/changeLoginEmail.ts) ────

/** The server's reason, without Firebase's "functions/…" wrapping. */
function plainError(err: unknown, fallback: string): Error {
  const message = (err as { message?: string })?.message
  return new Error(message && !/^internal$/i.test(message) ? message : fallback)
}

/** The email the member signs in with, and how (password, google.com…). Owner/Admin only. */
export async function getMemberLoginEmail(
  uid: string
): Promise<{ loginEmail: string; providers: string[] }> {
  try {
    return (
      await httpsCallable<
        { schoolId: string; uid: string },
        { loginEmail: string; providers: string[] }
      >(
        functions,
        'getMemberLoginEmail'
      )({ schoolId: UseCustomerStore().selectedStoreCode, uid })
    ).data
  } catch (err) {
    throw plainError(err, 'The login email could not be read.')
  }
}

/**
 * Changes the member's login email AND account email to `newEmail`. The old
 * address is told; the new one gets a password reset. Owner/Admin at every
 * school the member belongs to. `warnings`: an email that couldn't be sent.
 */
export async function changeMemberLoginEmail(
  uid: string,
  newEmail: string
): Promise<{ loginEmail: string; changed: boolean; warnings: string[] }> {
  try {
    return (
      await httpsCallable<
        { schoolId: string; uid: string; newEmail: string },
        { loginEmail: string; changed: boolean; warnings: string[] }
      >(
        functions,
        'changeMemberLoginEmail'
      )({ schoolId: UseCustomerStore().selectedStoreCode, uid, newEmail })
    ).data
  } catch (err) {
    throw plainError(err, 'The login email could not be changed. Try again.')
  }
}
